// Lämpökartan Leaflet-taso: piirtää solut yhdelle canvasille.
//
// Tuhansia Leaflet-suorakulmioita olisi raskas, joten solut piirretään suoraan
// kuvaan. Canvas on kartan kokoinen ja siirtyy paikalleen jokaisen liikkeen
// päätteeksi. Zoomauksen ajaksi se piilotetaan, koska Leafletin zoom-animaatio
// ei skaalaa omia canvas-kerroksia oikein; uusi kuva piirretään zoomin jälkeen.
// Data tulee palvelimelta kartan nykyiseltä alueelta (/api/heatmap).

import L from "leaflet";

import { cellRect } from "./geo.js";
import { heatStep } from "./heatColors.js";

export const HeatLayer = L.Layer.extend({
  options: { pane: "overlayPane", opacity: 0.72 },

  initialize(options) {
    L.setOptions(this, options);
    this._data = null;
  },

  /** Vaihtaa piirrettävät solut: { zoom, cells: [[x, y, n], ...] } tai null. */
  setData(data) {
    this._data = data;
    this._draw();
  },

  onAdd(map) {
    this._canvas = L.DomUtil.create("canvas", "heat-canvas");
    this._canvas.setAttribute("aria-hidden", "true");
    map.getPane(this.options.pane).appendChild(this._canvas);
    map.on("moveend resize zoomend", this._reset, this);
    map.on("zoomstart", this._hide, this);
    this._reset();
  },

  onRemove(map) {
    map.off("moveend resize zoomend", this._reset, this);
    map.off("zoomstart", this._hide, this);
    this._canvas.remove();
    this._canvas = null;
  },

  _hide() {
    if (this._canvas) this._canvas.style.visibility = "hidden";
  },

  _reset() {
    const map = this._map;
    const canvas = this._canvas;
    if (!map || !canvas) return;
    const size = map.getSize();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(size.x * ratio);
    canvas.height = Math.round(size.y * ratio);
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    canvas.style.visibility = "visible";
    this._draw();
  },

  _draw() {
    const map = this._map;
    const canvas = this._canvas;
    if (!map || !canvas) return;
    const ctx = canvas.getContext("2d");
    const ratio = canvas.width / Math.max(1, map.getSize().x);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!this._data?.cells?.length) return;

    const zoom = map.getZoom();
    const origin = map.getPixelBounds().min;
    const view = map.getSize();
    ctx.globalAlpha = this.options.opacity;
    for (const [x, y, n] of this._data.cells) {
      const { left, top, size } = cellRect(x, y, this._data.zoom, zoom, origin);
      if (left > view.x || top > view.y || left + size < 0 || top + size < 0) continue;
      ctx.fillStyle = heatStep(n).color;
      // Pieni limitys estää hiusraot vierekkäisten solujen välillä.
      ctx.fillRect(left, top, size + 0.6, size + 0.6);
    }
  },
});
